import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from '../lib/auth';
import { SetPassword } from '../pages/SetPassword';
import { AccountPaused } from '../pages/AccountPaused';
import { TwoFactorCard } from './TwoFactorCard';

export function Splash() {
  return (
    <div className="flex h-full items-center justify-center" aria-busy="true" aria-label="Loading">
      <div className="flex items-center gap-3 text-slate-500">
        <div className="flex size-9 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">AI</div>
        <span className="animate-pulse">Loading…</span>
      </div>
    </div>
  );
}

/** The server didn't answer while opening the app. The login is kept; we retry every few seconds. */
function Unavailable() {
  const { retry } = useAuth();
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-sm text-center">
        <div className="mx-auto flex size-10 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">AI</div>
        <h1 className="mt-4 text-lg font-semibold text-slate-900">Can’t reach AI CRM right now</h1>
        <p className="mt-2 text-sm text-slate-600">Please check your internet connection. We’ll keep trying and open the app as soon as it’s back.</p>
        <button type="button" onClick={retry} className="mt-5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700">
          Try again now
        </button>
      </div>
    </div>
  );
}

/** The company requires two-step sign-in for Admins: set it up before anything else. */
function Require2fa() {
  const { logout } = useAuth();
  return (
    <div className="flex min-h-full items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-lg space-y-4">
        <h1 className="text-xl font-semibold">One more step to keep your company safe</h1>
        <TwoFactorCard required />
        <button type="button" onClick={() => void logout()} className="text-sm text-slate-500 hover:underline">Sign out</button>
      </div>
    </div>
  );
}

export function RequireSignedOut() {
  const { status, me } = useAuth();
  if (status === 'loading') return <Splash />;
  if (status === 'unavailable') return <Unavailable />;
  if (status === 'signedIn') return <Navigate to={me?.isPlatformAdmin ? '/platform' : '/'} replace />;
  return <Outlet />;
}

/** Sends people to the right place: sign-in, set-your-password, paused account, or their area. */
export function RequireSignedIn({ area }: { area: 'tenant' | 'platform' }) {
  const { status, me } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <Splash />;
  if (status === 'unavailable') return <Unavailable />;
  if (status === 'signedOut' || !me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (me.mustChangePassword) return <SetPassword />;
  if (me.twoFactorSetupRequired) return <Require2fa />;
  if (area === 'tenant' && me.isPlatformAdmin) return <Navigate to="/platform" replace />;
  if (area === 'platform' && !me.isPlatformAdmin) return <Navigate to="/" replace />;
  if (area === 'tenant' && me.tenant?.status === 'suspended') return <AccountPaused />;
  if (area === 'tenant' && me.tenant?.status === 'cancelled') return <AccountPaused />;
  return <Outlet />;
}
